import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Mail, Clock, Shield, RefreshCw, XCircle } from 'lucide-react';
import { formatExpiryWindow } from '../../utils/dateFormat';

export interface InvitationInfo {
  invitationId: string;
  email: string;
  role: string;
  expiresAt?: string;
  expiryHours?: number;
  organizationId?: string;
  organizationName?: string;
  isExpired: boolean;
}

export interface ResendInvitationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invitationInfo: InvitationInfo;
  onResend: () => Promise<void>;
  /** Withdraws the existing invitation so the email can be invited again from scratch. */
  onCancelInvitation?: () => Promise<void>;
  /**
   * Whether the caller may act on this invitation (remind or cancel). When false the dialog only
   * explains why the invite was refused.
   */
  canManage?: boolean;
  /**
   * Superadmins revive an expired invitation; every other console role renews it through resend.
   * Only the wording differs.
   */
  expiredActionLabel?: string;
  isLoading?: boolean;
}

const formatDate = (dateString?: string): string => {
  if (!dateString) return 'Unknown date';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatRole = (role: string): string => {
  const roleNames: Record<string, string> = {
    superadmin: 'Super Admin',
    admin: 'Administrator',
    operator: 'Operator',
    staff: 'Staff',
  };
  return roleNames[role] || role;
};

/**
 * ResendInvitationDialog Component
 *
 * Dialog shown when attempting to invite a user who already has a pending or expired invitation.
 * Offers to send a reminder (resend, or revive when expired) or to cancel the existing invitation.
 */
export const ResendInvitationDialog: React.FC<ResendInvitationDialogProps> = ({
  open,
  onOpenChange,
  invitationInfo,
  onResend,
  onCancelInvitation,
  canManage = true,
  expiredActionLabel = 'Revive & Send Invitation',
  isLoading = false,
}) => {
  const handleResend = async () => {
    try {
      await onResend();
      onOpenChange(false);
    } catch (error) {
      // Error handling is done in parent component
      console.error('Failed to resend invitation:', error);
    }
  };

  const handleCancelInvitation = async () => {
    if (!onCancelInvitation) return;
    try {
      await onCancelInvitation();
      onOpenChange(false);
    } catch (error) {
      // Error handling is done in parent component
      console.error('Failed to cancel invitation:', error);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
  };

  const isExpired = invitationInfo.isExpired;
  const expiryWindow = invitationInfo.expiryHours !== undefined
    ? formatExpiryWindow(invitationInfo.expiryHours)
    : undefined;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-blue-600">
            {isExpired ? (
              <>
                <Clock className="h-5 w-5" />
                Invitation Expired
              </>
            ) : (
              <>
                <Mail className="h-5 w-5" />
                Invitation Already Exists
              </>
            )}
          </DialogTitle>
          <DialogDescription className="text-base pt-2">
            {!canManage
              ? 'An invitation to this email has not been accepted yet. It sits outside what your role can change, so ask a platform administrator to resend or cancel it.'
              : isExpired
                ? 'A previous invitation to this email has expired and was never accepted. Send it again, or cancel it to start over.'
                : 'An invitation to this email has not been accepted yet. Send a reminder, or cancel it to start over.'}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          <div className="bg-slate-50 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-3">
              <Mail className="h-5 w-5 text-slate-500" />
              <div>
                <p className="text-sm text-slate-600">Email</p>
                <p className="font-medium text-slate-900">{invitationInfo.email}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Shield className="h-5 w-5 text-slate-500" />
              <div>
                <p className="text-sm text-slate-600">Role</p>
                <p className="font-medium text-slate-900">{formatRole(invitationInfo.role)}</p>
              </div>
            </div>

            {invitationInfo.organizationName && (
              <div className="flex items-center gap-3">
                <svg className="h-5 w-5 text-slate-500" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                  <path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <div>
                  <p className="text-sm text-slate-600">Organization</p>
                  <p className="font-medium text-slate-900">{invitationInfo.organizationName}</p>
                </div>
              </div>
            )}

            <div className="flex items-center gap-3">
              <Clock className="h-5 w-5 text-slate-500" />
              <div>
                <p className="text-sm text-slate-600">
                  {isExpired ? 'Expired on' : 'Expires on'}
                </p>
                <p className={`font-medium ${isExpired ? 'text-red-600' : 'text-slate-900'}`}>
                  {formatDate(invitationInfo.expiresAt)}
                </p>
              </div>
            </div>
          </div>

          {canManage && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
            <RefreshCw className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-800">
              <p className="font-medium">What happens next?</p>
              <p className="mt-1">
                {isExpired
                  ? `The invitation will be revived${expiryWindow ? ` and will expire in ${expiryWindow}` : ' with a new expiration'}. A fresh invitation email will be sent.`
                  : `A new invitation email will be sent with the same details. The expiration will be extended${expiryWindow ? ` by ${expiryWindow}` : ''}.`}
              </p>
              {onCancelInvitation && (
                <p className="mt-2">
                  Cancelling withdraws this invitation: its link stops working, and you can invite the
                  email again.
                </p>
              )}
            </div>
          </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
          >
            Close
          </Button>
          {canManage && onCancelInvitation && (
            <Button
              type="button"
              variant="outline"
              onClick={handleCancelInvitation}
              disabled={isLoading}
              className="border-red-300 text-red-700 hover:bg-red-50"
            >
              <XCircle className="h-4 w-4 mr-1" />
              Cancel Invitation
            </Button>
          )}
          {canManage && (
          <Button
            type="button"
            onClick={handleResend}
            disabled={isLoading}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {isLoading ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                {isExpired ? 'Sending...' : 'Sending reminder...'}
              </>
            ) : isExpired ? (
              expiredActionLabel
            ) : (
              'Send Reminder'
            )}
          </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
